import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Input, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import LinkV2 from "components/common/LinkV2";
import { PATH } from "routes/paths";
import { ambassadorApi, getAmbassadorToken } from "api/ambassadorApi";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CodeStatusChip from "pages/ambassador/CodeStatusChip";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 독립 웹 페이지 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

// 프로필 정보 — 활동명(링크 노출 이름) 수정 전용 페이지 (계정 관리 메뉴에서 진입).
// 링크 주소(코드)는 이미 업로드된 콘텐츠 속 링크와 호환되지 않아 변경 불가 — 담당자 문의로 안내한다.
export default function AmbassadorLink() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [codeInfos, setCodeInfos] = useState<
    { code: string; isActive: boolean }[]
  >([]);
  const [name, setName] = useState("");
  // 저장 버튼 활성화 기준 — 불러온 활동명에서 실제로 바뀌었을 때만 저장 가능
  const [initialName, setInitialName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // 내 링크 풀 URL 복사 — 홈과 동일한 동작
  const copyLink = async (code: string) => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/with/${code}`
      );
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch (e) {
      // clipboard 미지원 브라우저 — 링크 텍스트가 select-all 이라 수동 복사 가능
    }
  };

  const hasToken = !!getAmbassadorToken();

  useEffect(() => {
    if (!hasToken) return;
    (async () => {
      try {
        const [dashboardRes, contractRes] = await Promise.all([
          ambassadorApi.dashboard(),
          ambassadorApi.getContract(),
        ]);
        if (dashboardRes.code === 0 && dashboardRes.result) {
          setCodeInfos(dashboardRes.result.codeInfos ?? []);
        }
        // 활동명 단일 소스(계정 name) — 대시보드 우선, 실패 시 계약 조회 응답의 name
        const registeredName =
          (dashboardRes.code === 0 ? dashboardRes.result?.name : null) ??
          (contractRes.code === 0 ? contractRes.result?.name : null) ??
          "";
        setName(registeredName);
        setInitialName(registeredName);
      } catch (e) {
        // 401 은 인터셉터가 로그인으로 보낸다
      } finally {
        setLoading(false);
      }
    })();
  }, [hasToken]);

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  const canSubmit =
    !submitting &&
    !loading &&
    name.trim().length > 0 &&
    name.trim() !== initialName;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ambassadorApi.updateName(name.trim());
      if (res.code === 0) {
        navigate(PATH.ambassador);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "480px",
          px: "20px",
          py: "40px",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <LinkV2
            variant="Caption"
            text="← 홈으로"
            onClick={() => navigate(PATH.ambassador)}
          />
        </Box>
        <Stack spacing={"8px"} sx={{ mt: 2 }}>
          <CustomTypography
            variant="H3"
            sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
          >
            {"프로필 정보"}
          </CustomTypography>
          <CustomTypography
            variant="Caption"
            sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
          >
            {"링크 방문자에게 보여지는 활동명을 수정할 수 있어요."}
          </CustomTypography>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: "80px" }}>
            <CircularProgress color="neutral" />
          </Box>
        ) : (
          <Stack spacing={4} sx={{ mt: 4 }}>
            <Stack spacing={"6px"}>
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"활동명"}
              </CustomTypography>
              <Input
                sx={inputSx}
                slotProps={{ input: { maxLength: 11 } }}
                placeholder="링크 방문자에게 보여지는 이름 (12자 미만)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Stack>

            <Stack spacing={"6px"}>
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"링크 주소"}
              </CustomTypography>
              {codeInfos.length === 0 ? (
                // 링크 항목은 항상 보여주되, 아직 없으면 생성 조건을 안내한다
                <CustomTypography
                  variant="Caption"
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Medium,
                  }}
                >
                  {"아직 만든 링크가 없어요. 계약 완료 후에 만들 수 있어요."}
                </CustomTypography>
              ) : (
                <>
                  {codeInfos.map(({ code, isActive }) => (
                    <Stack
                      key={code}
                      direction="row"
                      alignItems="center"
                      spacing={"8px"}
                    >
                      <CustomTypography
                        variant="Caption"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Medium,
                          userSelect: "all",
                          wordBreak: "break-all",
                        }}
                      >
                        {`${window.location.origin}/with/${code}`}
                      </CustomTypography>
                      {/* 링크 복사 — 항상 노출, 복사 직후엔 체크 아이콘으로 피드백 */}
                      {copiedCode === code ? (
                        <CheckRoundedIcon
                          sx={{
                            flexShrink: 0,
                            fontSize: "18px",
                            color: "#349434",
                          }}
                        />
                      ) : (
                        <ContentCopyRoundedIcon
                          titleAccess="링크 복사"
                          onClick={() => copyLink(code)}
                          sx={{
                            flexShrink: 0,
                            fontSize: "18px",
                            color: theme.vars.palette.Content.Medium,
                            cursor: "pointer",
                          }}
                        />
                      )}
                      {/* 자동 승인 체제라 "승인 완료"는 불필요한 정보 — 비활성(대기) 상태만 표시한다 */}
                      {!isActive && <CodeStatusChip isActive={false} />}
                    </Stack>
                  ))}
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {
                      "링크 주소는 만든 후 변경할 수 없어요. 변경이 꼭 필요하면 담당자에게 문의해 주세요."
                    }
                  </CustomTypography>
                </>
              )}
            </Stack>

            {error && (
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.HighlightWithAlert,
                }}
              >
                {error}
              </CustomTypography>
            )}

            <Button
              variant="Action"
              size="lg"
              loading={submitting}
              disabled={!canSubmit}
              onClick={handleSubmit}
              sx={{
                width: "100%",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"저장하기"}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
